import { zodResolver } from '@hookform/resolvers/zod';
import type { HeadersFunction } from '@remix-run/node';
import { useSearchParams, useNavigate, Link, type MetaFunction } from '@remix-run/react';
import { useState, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AuthLeftPanel from '~/components/account/AuthLeftPanel';
import { useCustomer } from '~/context/CustomerContext';
import { useFlashMessages } from '~/context/FlashMessagesContext';
import { pushLogin, pushUserData, createUserData, useVisitorCookie } from '~/gtm';
import { useLocale } from '~/hooks/useLocale';
import { useRoutePath } from '~/hooks/useRoutePath';
import Default from '~/layouts/Default';
import { loginSchema, type LoginFormValues } from '~/schemas/login.schema';
import { apiFetch } from '~/utils/apiFetch';
import { privatePageHeaders } from '~/utils/cacheControl.server';
import { buildPageMeta } from '~/utils/meta';

const getApiUrl = (): string => {
  if (typeof window !== 'undefined' && window.ENV?.API_URL) {
    return window.ENV.API_URL;
  }
  return '';
};

export const meta: MetaFunction = ({ matches }) => buildPageMeta('Přihlášení', matches);

export const headers: HeadersFunction = ({ parentHeaders }) => privatePageHeaders(parentHeaders);

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addMessage } = useFlashMessages();
  const { refetchCustomer, setCustomerUrl } = useCustomer();

  const resetRequestedShown = useRef(false);
  const resetSuccessShown = useRef(false);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const locale = useLocale();
  const routePath = useRoutePath();
  const visitorType = useVisitorCookie();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  useEffect(() => {
    if (!resetRequestedShown.current && searchParams.get('resetRequested')) {
      resetRequestedShown.current = true;
      addMessage('success', t('pages.login.reset_requested_message'));
      navigate(routePath('login'), { replace: true });
    }
  }, [searchParams, addMessage, navigate, locale, routePath, t]);

  useEffect(() => {
    if (!resetSuccessShown.current && searchParams.get('resetSuccessful')) {
      resetSuccessShown.current = true;
      addMessage('success', t('pages.login.reset_success_message'));
      navigate(routePath('login'), { replace: true });
    }
  }, [searchParams, addMessage, navigate, locale, routePath, t]);

  const onSubmit = async (formData: LoginFormValues) => {
    setLoading(true);
    setError(null);

    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        setError(t('pages.login.errors.api_not_configured'));
        return;
      }

      const response = await apiFetch(
        `${apiUrl}/api/v2/shop/customers/token`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email, password: formData.password }),
        },
        { skipAuthRedirect: true }
      );

      let data: { customer?: string; message?: string };
      try {
        data = await response.json();
      } catch {
        setError(t('pages.login.errors.unexpected_error'));
        return;
      }

      if (!response.ok) {
        setError(data.message || t('pages.login.errors.invalid_credentials'));
        return;
      }

      // JWT token is now set as HttpOnly cookie by the server
      // Store the customer URL for CustomerContext to use
      if (data.customer) {
        setCustomerUrl(data.customer);
      }

      // Fetch customer data first to get all available fields
      const customerData = await refetchCustomer();

      // GTM: Push login event
      pushLogin('email');

      // GTM: Push user_data for Enhanced Conversions
      // Extract user_id from customer URL (format: /api/v2/shop/customers/123)
      const userId = data.customer?.split('/').pop();
      const userData = createUserData({
        userId,
        email: customerData?.email || formData.email,
        phone: customerData?.phoneNumber || undefined,
        firstName: customerData?.firstName || undefined,
        lastName: customerData?.lastName || undefined,
      });
      pushUserData(userData, 'loggedin', visitorType);

      navigate(routePath('accountDashboard'), { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError(t('pages.login.errors.unexpected_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Default>
      <div className="container my-auto">
        <div className="row py-4">
          <div className="col-12 col-sm-10 offset-sm-1 col-md-8 offset-md-2 col-lg-6 offset-lg-0 col-xl-4 offset-xl-1 order-lg-1">
            <div className="d-flex justify-content-center align-items-center h-100 px-3">
              <div className="w-100 py-lg-5 mb-5 my-lg-5">
                <h1 className="h2 mb-5">{t('pages.login.title')}</h1>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmit)(e);
                  }}
                  noValidate
                >
                  {error && (
                    <div className="alert alert-danger">
                      <div className="fw-bold">{t('common.error')}</div>
                      {error}
                    </div>
                  )}

                  <div className="mb-5">
                    <div className="field mb-3 required">
                      <label htmlFor="_email" className="form-label required">
                        {t('components.form.login.email')}
                      </label>
                      <input
                        type="email"
                        id="_email"
                        required
                        autoComplete="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        {...register('email')}
                      />
                      {errors.email && <div className="invalid-feedback">{t(errors.email.message as string)}</div>}
                    </div>

                    <div className="field mb-3 required">
                      <label htmlFor="_password" className="form-label required">
                        {t('pages.login.password')}
                      </label>
                      <input
                        type="password"
                        id="_password"
                        required
                        autoComplete="current-password"
                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                        {...register('password')}
                      />
                      {errors.password && (
                        <div className="invalid-feedback">{t(errors.password.message as string)}</div>
                      )}
                    </div>

                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="_remember_me"
                        name="_remember_me"
                        className="form-check-input"
                        value="1"
                      />
                      <label className="form-check-label" htmlFor="_remember_me">
                        {t('pages.login.remember_me')}
                      </label>
                    </div>
                  </div>

                  <div className="d-grid mb-2">
                    <Button type="submit" id="login-button" disabled={loading}>
                      {loading ? t('pages.login.logging_in') : t('pages.login.submit')}
                    </Button>
                  </div>
                </form>

                <div className="d-grid">
                  <Link to={routePath('forgottenPassword')} className="btn btn-link" rel="nofollow">
                    {t('pages.login.forgot_password')}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <AuthLeftPanel />
        </div>
      </div>
    </Default>
  );
}
